import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import colors, { useColors } from 'app/utils/colors';
import { updatePassword } from 'app/services/company/userService';

const getToken = () => localStorage.getItem('access_token');

const EyeIcon = ({ visible }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8">
    {!visible ? (
      <>
        <path d="M3 3l18 18" strokeLinecap="round" />
        <path
          d="M10.6 5.6A10.7 10.7 0 0 1 12 5.5c5 0 9 4 10.5 6.5-.6 1-1.5 2.2-2.7 3.3M6.6 6.9C4.4 8.3 2.9 10.2 2 12c1.5 2.5 5.5 6.5 10 6.5 1.3 0 2.6-.3 3.8-.9"
          strokeLinecap="round"
        />
        <path d="M9.9 10a3 3 0 0 0 4.2 4.2" strokeLinecap="round" />
      </>
    ) : (
      <>
        <path d="M1.5 12S5.5 5.5 12 5.5 22.5 12 22.5 12 18.5 18.5 12 18.5 1.5 12 1.5 12z" strokeLinecap="round" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

export default function ChangePassword({ show, onClose }) {
  const dynamicColors = useColors();
  const { t } = useTranslation('settings');
  const user = useSelector((state) => state.auth.user);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetAndClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setError('');
    setLoading(false);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError(t('passwordChange.validation.passwordMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordChange.validation.confirmMismatch'));
      return;
    }

    setLoading(true);
    try {
      await updatePassword({ currentPassword, newPassword });
      resetAndClose();
    } catch (err) {
      if (err.response?.status === 400) {
        setError(t('passwordChange.validation.currentPasswordIncorrect'));
      } else {
        setError(t('passwordChange.alerts.changeError'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={styles.overlay} onClick={resetAndClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div style={styles.header}>
          <h2 style={styles.title}>{t('passwordChange.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={resetAndClose}
            style={styles.closeBtn}
          >
            ×
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={styles.scrollArea}>
          <form onSubmit={handleSubmit} style={styles.body}>
            <div style={styles.infoBox}>{t('passwordChange.infoBox')}</div>

            {/* Scoped hidden username field so password managers associate credentials inside this form */}
            <input
              type="email"
              name="username"
              value={user?.email || ""}
              autoComplete="username"
              readOnly
              tabIndex={-1}
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                border: 0,
                opacity: 0,
                pointerEvents: 'none'
              }}
            />

            <label style={styles.label}>{t('passwordChange.fields.currentPassword')}</label>
            <div style={styles.inputWrapper}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                name="current-password"
                id="current-password"
                autoComplete="current-password"
                placeholder={t('passwordChange.placeholders.currentPassword')}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((prev) => !prev)}
                tabIndex={-1}
                aria-label={showCurrentPassword ? "Masquer" : "Afficher"}
                style={styles.eyeBtn}
              >
                <EyeIcon visible={showCurrentPassword} />
              </button>
            </div>

            <label style={styles.label}>{t('passwordChange.fields.newPassword')}</label>
            <div style={styles.inputWrapper}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                name="new-password"
                id="new-password"
                autoComplete="new-password"
                placeholder={t('passwordChange.placeholders.newPassword')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                tabIndex={-1}
                aria-label={showNewPassword ? "Masquer" : "Afficher"}
                style={styles.eyeBtn}
              >
                <EyeIcon visible={showNewPassword} />
              </button>
            </div>

            <label style={styles.label}>{t('passwordChange.fields.confirmPassword')}</label>
            <div style={styles.inputWrapper}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm-password"
                id="confirm-password"
                autoComplete="new-password"
                placeholder={t('passwordChange.placeholders.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                tabIndex={-1}
                aria-label={showConfirmPassword ? "Masquer" : "Afficher"}
                style={styles.eyeBtn}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? t('passwordChange.actions.submitting') : t('passwordChange.actions.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px',
    background: '#f8f8f8',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '19px',
    fontWeight: '500',
    color: '#1a1a2e',
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: '1px solid #9ca3af',
    background: 'transparent',
    color: '#4b5563',
    fontSize: '16px',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
  },
  scrollArea: {
    overflowY: 'auto',
  },
  body: {
    padding: '24px 28px 28px',
  },
  infoBox: {
    background: '#f3f4f8',
    borderRadius: '6px',
    padding: '16px 18px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: 'var(--theme-primary, #f95f1f)',
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    color: '#374151',
    marginBottom: '8px',
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '11px 40px 11px 14px',
    fontSize: '14px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    background: '#fafafa',
    transition: 'border-color 0.2s',
  },
  eyeBtn: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9CA3AF',
  },
  btn: {
    width: '100%',
    padding: '13px',
    background: 'var(--theme-primary, #f95f1f)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s, opacity 0.2s',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    margin: '-8px 0 16px',
    padding: '10px 14px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#dc2626',
  },
};